import { getProject, linkHost, plainProjectName, projects, type Project, type ProjectSlug } from './projects';
import { techStack, type TechLogo } from './techStack';

export type ServiceId = 'apps' | 'websites' | 'software' | 'ai';

export type AiTask = 'screens' | 'moderation' | 'summaries' | 'translation' | 'nameChecks';

export type ServiceShowcase =
    | { kind: 'app'; slug: ProjectSlug; name: string; iconSrc: string }
    | { kind: 'website'; slug: ProjectSlug; host: string; screenshotSrc: string }
    | { kind: 'technology'; logo: TechLogo }
    | { kind: 'aiTask'; task: AiTask; iconSrc: string };

export interface Service {
    id: ServiceId;
    path: string;
    showcase: ServiceShowcase[];
}

const SOFTWARE_TECHNOLOGIES = ['Go', '.NET', 'TypeScript', 'React', 'Vue.js', 'Vite'];

const AI_TASKS: { task: AiTask; slug: ProjectSlug }[] = [
    { task: 'screens', slug: 'design' },
    { task: 'moderation', slug: 'sunup' },
    { task: 'summaries', slug: 'launchx' },
    { task: 'translation', slug: 'launchx' },
    { task: 'nameChecks', slug: 'slingpin' }
];

function appShowcase(): ServiceShowcase[] {
    return projects
        .filter((project) => project.category === 'app')
        .map((project) => ({ kind: 'app', slug: project.slug, name: plainProjectName(project), iconSrc: project.iconSrc }));
}

function websiteShowcase(): ServiceShowcase[] {
    return projects.flatMap((project: Project): ServiceShowcase[] => {
        const website = project.links.find((link) => link.kind === 'website');
        if (!website || !project.websiteSrc) return [];
        return [{ kind: 'website', slug: project.slug, host: linkHost(website), screenshotSrc: project.websiteSrc }];
    });
}

function technologyShowcase(): ServiceShowcase[] {
    return SOFTWARE_TECHNOLOGIES.map((name) => {
        const logo = techStack.find((technology) => technology.name === name);
        if (!logo) throw new Error(`Missing tech logo "${name}"`);
        return { kind: 'technology', logo };
    });
}

function aiShowcase(): ServiceShowcase[] {
    return AI_TASKS.map(({ task, slug }) => ({ kind: 'aiTask', task, iconSrc: getProject(slug).iconSrc }));
}

export const services: Service[] = [
    { id: 'apps', path: '/services#apps', showcase: appShowcase() },
    { id: 'websites', path: '/services#websites', showcase: websiteShowcase() },
    { id: 'software', path: '/services#software', showcase: technologyShowcase() },
    { id: 'ai', path: '/services#ai', showcase: aiShowcase() }
];
